import { isRecord } from '../../../../utils/typeGuards';
import { getBindingCatalog } from '../runtime/dataServices/bindingCatalog';
import type {
    ActiveDataBinding,
    BindingId,
    RuntimeResultLinkDescriptor,
} from '../runtime/dataServices/types';

const RUNTIME_BINDING_ALLOWED_KEYS = ['bindingId', 'arg'] as const;

interface ParseOptionalRuntimeBindingArgs<TBinding extends ActiveDataBinding = ActiveDataBinding> {
    value: unknown;
    bindingPath: string;
    resolveBindingById: (bindingId: BindingId, arg?: string) => TBinding;
}

export function assertNoUnknownKeys(
    value: Record<string, unknown>,
    allowedKeys: readonly string[],
    path: string
): void {
    Object.keys(value).forEach((key) => {
        if (!allowedKeys.includes(key)) {
            throw new Error(`Unsupported "${path}.${key}" key.`);
        }
    });
}

export function normalizeOptionalString(value: unknown, path: string): string | undefined {
    if (typeof value === 'undefined' || value === null) {
        return undefined;
    }

    if (typeof value !== 'string') {
        throw new Error(`Expected "${path}" to be a string.`);
    }

    const trimmed = value.trim();
    if (!trimmed.length) {
        return undefined;
    }

    return trimmed;
}

export function normalizeRequiredString(value: unknown, path: string): string {
    const normalized = normalizeOptionalString(value, path);
    if (typeof normalized === 'undefined') {
        throw new Error(`Expected "${path}" to be a non-empty string.`);
    }

    return normalized;
}

export function parseOptionalRuntimeBinding<
    TBinding extends ActiveDataBinding = ActiveDataBinding
>({
    value,
    bindingPath,
    resolveBindingById,
}: ParseOptionalRuntimeBindingArgs<TBinding>): TBinding | null {
    if (value === null) {
        return null;
    }

    if (typeof value === 'undefined') {
        throw new Error(`Expected "${bindingPath}" to be null or an object.`);
    }

    if (!isRecord(value)) {
        throw new Error(`Expected "${bindingPath}" to be an object when provided.`);
    }

    assertNoUnknownKeys(value, RUNTIME_BINDING_ALLOWED_KEYS, bindingPath);

    const bindingId = normalizeRequiredString(value.bindingId, `${bindingPath}.bindingId`);
    const arg = normalizeOptionalString(value.arg, `${bindingPath}.arg`);

    return resolveBindingById(bindingId as BindingId, arg);
}

interface ParseRuntimeBindingFromSettingsByValueKindArgs<
    TValueKind extends RuntimeResultLinkDescriptor['valueKind']
> {
    settings: Record<string, unknown>;
    settingsPath: string;
    valueKind: TValueKind;
}

export function parseRuntimeBindingFromSettingsByValueKind<
    TValueKind extends RuntimeResultLinkDescriptor['valueKind']
>({
    settings,
    settingsPath,
    valueKind,
}: ParseRuntimeBindingFromSettingsByValueKindArgs<TValueKind>): ActiveDataBinding | null {
    if (!Object.hasOwn(settings, 'binding')) {
        throw new Error(`Expected "${settingsPath}.binding" to be explicitly defined.`);
    }

    const bindingValue = settings.binding;
    if (bindingValue === null) {
        return null;
    }

    if (typeof bindingValue === 'undefined') {
        throw new Error(`Expected "${settingsPath}.binding" to be null or an object.`);
    }

    if (!isRecord(bindingValue)) {
        throw new Error(`Expected "${settingsPath}.binding" to be an object when provided.`);
    }

    assertNoUnknownKeys(bindingValue, RUNTIME_BINDING_ALLOWED_KEYS, `${settingsPath}.binding`);

    const bindingId = normalizeRequiredString(
        bindingValue.bindingId,
        `${settingsPath}.binding.bindingId`
    ) as BindingId;
    const arg = normalizeOptionalString(bindingValue.arg, `${settingsPath}.binding.arg`);

    const descriptor = getBindingCatalog().getByIdAndValueKindOrThrow(bindingId, valueKind);
    if (descriptor.link.argPolicy === 'required' && typeof arg === 'undefined') {
        throw new Error(
            `Expected "${settingsPath}.binding.arg" to be a non-empty string for "${bindingId}".`
        );
    }

    return getBindingCatalog().toActiveDataBinding(descriptor, arg);
}
